import React, { useState } from 'react';
import { Car } from 'lucide-react';
import { SEO } from '../components/SEO';

interface VehicleState {
  vehicleValue: string;
  vehicleType: 'car' | 'motorcycle' | 'truck';
  buyerType: 'private' | 'company';
  sellerType: 'private' | 'company';
  country: 'HR' | 'BA' | 'RS';
}

const countryConfigs = {
  HR: {
    currency: 'EUR',
    symbol: '€',
    taxRate: 0.05, // 5% special tax
    adminFee: 35, // Administrative fee
    plates: 25, // New plates cost
    registration: 50, // Registration fee
    technicalReview: 40, // Technical review fee
  },
  BA: {
    currency: 'BAM',
    symbol: 'KM',
    taxRate: 0.05, // 5% transfer tax
    adminFee: 20, // Administrative fee
    plates: 60, // New plates cost
    registration: 150, // Registration fee
    technicalReview: 80, // Technical review fee
  },
  RS: {
    currency: 'RSD',
    symbol: 'RSD',
    taxRate: 0.025, // 2.5% transfer tax
    adminFee: 2000, // Administrative fee
    plates: 4500, // New plates cost
    registration: 7500, // Registration fee
    technicalReview: 6800, // Technical review fee
  }
};

function KalkulatorPrijepisaVozila() {
  const [values, setValues] = useState<VehicleState>({
    vehicleValue: '',
    vehicleType: 'car',
    buyerType: 'private',
    sellerType: 'private',
    country: 'HR'
  });

  const [results, setResults] = useState<{
    tax: number;
    adminFee: number;
    plates: number;
    registration: number;
    technicalReview: number;
    total: number;
  } | null>(null);

  const calculateCosts = () => {
    const config = countryConfigs[values.country];
    const value = parseFloat(values.vehicleValue);
    
    if (isNaN(value)) {
      return null;
    }

    // Calculate tax based on vehicle value and transaction type
    let tax = 0;
    if (values.buyerType === 'private' && values.sellerType === 'company') {
      tax = value * config.taxRate;
    }

    // Calculate total costs
    const total = {
      tax,
      adminFee: config.adminFee,
      plates: config.plates,
      registration: config.registration,
      technicalReview: config.technicalReview,
      total: tax + config.adminFee + config.plates + config.registration + config.technicalReview
    };

    setResults(total);
  };

  const handleInputChange = (field: keyof VehicleState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getRequiredDocuments = () => {
    const documents = [
      'Osobna iskaznica prodavatelja i kupca',
      'Prometna dozvola',
      'Knjižica vozila',
      'Potvrda o tehničkoj ispravnosti (ne starija od 30 dana)',
      'Polica obveznog osiguranja',
    ];

    if (values.country === 'HR') {
      documents.push('Obrazac Ugovora o kupoprodaji vozila');
      if (values.buyerType === 'company') {
        documents.push('Izvod iz sudskog registra (za tvrtke)');
      }
    } else if (values.country === 'BA') {
      documents.push('Ovjereni kupoprodajni ugovor');
      documents.push('Potvrda o prebivalištu');
    } else if (values.country === 'RS') {
      documents.push('Ovjereni kupoprodajni ugovor');
      documents.push('Potvrda o odjavi vozila');
    }

    return documents;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Prijepisа Vozila",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte troškove prijepisа vozila. Uključuje sve naknade, poreze i pristojbe.",
    "url": "https://online-kalkulator.com/kalkulator-prijepisa-vozila",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun troškova prijepisа",
      "Popis potrebne dokumentacije",
      "Podrška za različite vrste vozila",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Prijepisа Vozila | Izračun Troškova Prijepisа"
        description="Izračunajte troškove prijepisа vozila. Uključuje sve naknade, poreze i pristojbe. Informacije o potrebnoj dokumentaciji za prijepis vozila."
        canonicalUrl="/kalkulator-prijepisa-vozila"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Car className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Prijepisа Vozila</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vrijednost vozila
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={values.vehicleValue}
                      onChange={(e) => handleInputChange('vehicleValue', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`Unesite iznos u ${countryConfigs[values.country].currency}`}
                    />
                    <span className="absolute right-3 top-2 text-gray-500">
                      {countryConfigs[values.country].symbol}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vrsta vozila
                  </label>
                  <select
                    value={values.vehicleType}
                    onChange={(e) => handleInputChange('vehicleType', e.target.value as VehicleState['vehicleType'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="car">Automobil</option>
                    <option value="motorcycle">Motocikl</option>
                    <option value="truck">Kamion</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Država
                  </label>
                  <select
                    value={values.country}
                    onChange={(e) => handleInputChange('country', e.target.value as VehicleState['country'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="HR">Hrvatska</option>
                    <option value="BA">Bosna i Hercegovina</option>
                    <option value="RS">Srbija</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kupac je
                  </label>
                  <select
                    value={values.buyerType}
                    onChange={(e) => handleInputChange('buyerType', e.target.value as VehicleState['buyerType'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="private">Fizička osoba</option>
                    <option value="company">Pravna osoba</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prodavatelj je
                  </label>
                  <select
                    value={values.sellerType}
                    onChange={(e) => handleInputChange('sellerType', e.target.value as VehicleState['sellerType'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="private">Fizička osoba</option>
                    <option value="company">Pravna osoba</option>
                  </select>
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={calculateCosts}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Izračunaj troškove
              </button>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Posebni porez:</span>
                      <span className="font-semibold">
                        {formatCurrency(results.tax)} {countryConfigs[values.country].symbol}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Administrativna pristojba:</span>
                      <span className="font-semibold">
                        {formatCurrency(results.adminFee)} {countryConfigs[values.country].symbol}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Nove registarske pločice:</span>
                      <span className="font-semibold">
                        {formatCurrency(results.plates)} {countryConfigs[values.country].symbol}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Registracija:</span>
                      <span className="font-semibold">
                        {formatCurrency(results.registration)} {countryConfigs[values.country].symbol}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Tehnički pregled:</span>
                      <span className="font-semibold">
                        {formatCurrency(results.technicalReview)} {countryConfigs[values.country].symbol}
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="text-gray-800 font-medium">Ukupni troškovi:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatCurrency(results.total)} {countryConfigs[values.country].symbol}
                    </span>
                  </div>
                </div>
              )}

              {/* Required Documents */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Potrebna dokumentacija:</h3>
                <ul className="space-y-2">
                  {getRequiredDocuments().map((doc, index) => (
                    <li key={index} className="flex items-center text-gray-600">
                      <span className="text-blue-500 mr-2">•</span>
                      {doc}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Prijepisа</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator prijepisа vozila pomaže vam izračunati sve troškove vezane uz prijenos
                  vlasništva nad vozilom. Uključuje sve potrebne pristojbe, naknade i poreze, te
                  pruža informacije o potrebnoj dokumentaciji.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Proces Prijepisа</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Priprema potrebne dokumentacije</li>
                  <li>Tehnički pregled vozila (ako je potrebno)</li>
                  <li>Ovjera kupoprodajnog ugovora</li>
                  <li>Plaćanje potrebnih pristojbi i poreza</li>
                  <li>Predaja dokumentacije u nadležnu službu</li>
                  <li>Izdavanje novih dokumenata i registarskih pločica</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Prijepis se mora obaviti u roku od 15 dana od kupnje</li>
                  <li>Sva dokumentacija mora biti originalna ili ovjerena</li>
                  <li>Tehnički pregled ne smije biti stariji od 30 dana</li>
                  <li>Vozilo mora biti osigurano prije prijepisа</li>
                  <li>Pristojbe se mogu razlikovati ovisno o lokalnoj samoupravi</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Prijepis</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Provjerite svu dokumentaciju prije kupnje vozila</li>
                  <li>Pripremite sve dokumente unaprijed</li>
                  <li>Provjerite ima li vozilo neplaćenih obveza</li>
                  <li>Dogovorite tko snosi troškove prijepisа</li>
                  <li>Zatražite pomoć stručne osobe ako niste sigurni</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Česti Problemi</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Nepotpuna dokumentacija</li>
                  <li>Neplaćene obveze na vozilu</li>
                  <li>Istekli tehnički pregled</li>
                  <li>Problemi s osiguranjem</li>
                  <li>Neusklađeni podaci u dokumentima</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorPrijepisaVozila;